// Defer Due Date Modal
// Allows users to postpone a compliance deadline with proper justification

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, differenceInDays } from 'date-fns';
import { Calendar as CalendarIcon, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Mock approvers - in real app, this would come from API
const MOCK_APPROVERS = [
  { value: '1', label: 'John Smith (Compliance Manager)' },
  { value: '2', label: 'Sarah Johnson (Operations Director)' },
  { value: '3', label: 'Mike Williams (Asset Manager)' },
];

// Form validation schema
const formSchema = z.object({
  newDueDate: z.date({
    message: 'New due date is required',
  }),
  reason: z.string()
    .min(20, 'Reason must be at least 20 characters'),
  approvalRequired: z.boolean().default(false),
  approver: z.string().optional(),
}).refine((data) => {
  // Approver must be selected if approval required
  if (data.approvalRequired && !data.approver) {
    return false;
  }
  return true;
}, {
  message: 'Approver must be selected when approval is required',
  path: ['approver'],
});

type FormValues = z.infer<typeof formSchema>;

interface DeferDueDateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: {
    id: number;
    assetName: string;
    regimeName: string;
    currentDueDate?: string;
    maxDeferralDays?: number;
  };
  onSubmit: (data: FormValues) => Promise<void>;
}

export function DeferDueDateModal({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: DeferDueDateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exceedsMaxDeferral, setExceedsMaxDeferral] = useState(false);

  const maxDeferralDays = complianceItem?.maxDeferralDays || 90; // Default 90 days

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newDueDate: undefined as unknown as Date,
      reason: '',
      approvalRequired: false,
      approver: '',
    },
  });

  const currentDueDate = complianceItem?.currentDueDate
    ? new Date(complianceItem.currentDueDate)
    : new Date();

  // Check if new date exceeds maximum deferral period
  const checkMaxDeferral = (date: Date) => {
    const daysDeferred = differenceInDays(date, currentDueDate);
    if (daysDeferred > maxDeferralDays) {
      setExceedsMaxDeferral(true);
    } else {
      setExceedsMaxDeferral(false);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    // Validate new due date is after current due date
    if (data.newDueDate <= currentDueDate) {
      toast.error('New due date must be after current due date');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(data);
      toast.success(
        data.approvalRequired
          ? 'Deferral request submitted for approval'
          : 'Due date deferred successfully'
      );
      form.reset();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to defer due date');
      console.error('Error deferring due date:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Defer Due Date</DialogTitle>
          <DialogDescription>
            {complianceItem ? (
              <>
                Postpone the compliance deadline for <strong>{complianceItem.assetName}</strong> - {complianceItem.regimeName}
              </>
            ) : (
              'Postpone a compliance deadline with proper justification'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Current Due Date (Read-only) */}
            <div className="rounded-lg border p-4 bg-muted/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Current Due Date</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {format(currentDueDate, 'PPP')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">Maximum Deferral</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {maxDeferralDays} days
                  </p>
                </div>
              </div>
            </div>

            {/* New Due Date */}
            <FormField
              control={form.control}
              name="newDueDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>New Due Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          if (date) checkMaxDeferral(date);
                        }}
                        disabled={(date) => date <= currentDueDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    {field.value && (
                      <>
                        Deferring by {differenceInDays(field.value, currentDueDate)} days
                      </>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Warning for exceeding max deferral */}
            {exceedsMaxDeferral && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The selected date exceeds the maximum deferral period of {maxDeferralDays} days. 
                  Approval will be required.
                </AlertDescription>
              </Alert>
            )}

            {/* Reason for Deferral */}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason for Deferral *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Provide a detailed reason for deferring this compliance deadline (minimum 20 characters)"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {field.value.length}/20 characters minimum
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Approval Required */}
            <FormField
              control={form.control}
              name="approvalRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value || exceedsMaxDeferral}
                      onCheckedChange={field.onChange}
                      disabled={exceedsMaxDeferral}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Approval required
                    </FormLabel>
                    <FormDescription>
                      {exceedsMaxDeferral
                        ? 'Approval is required because the deferral exceeds the maximum period'
                        : 'Check this if the deferral requires management approval'}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Approver Selection */}
            {(form.watch('approvalRequired') || exceedsMaxDeferral) && (
              <FormField
                control={form.control}
                name="approver"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Approver *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an approver" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {MOCK_APPROVERS.map((approver) => (
                          <SelectItem key={approver.value} value={approver.value}>
                            {approver.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select who should approve this deferral request
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {form.watch('approvalRequired') || exceedsMaxDeferral
                  ? 'Submit for Approval'
                  : 'Defer Due Date'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
